import { CommonRegistry } from '../components/components';
import {
  CommonFilteredComponentSystem,
  FilteredComponentSystemUpdateEntityArgs,
} from './SystemBase';

/**
 * Converts entity input state into velocity changes.
 * Reads input from the entity's Input component.
 *
 * On client: only processes local player (localPlayerOnly = true).
 * On server: processes all entities with the required components.
 */
export class MovementIntentSystem extends CommonFilteredComponentSystem {
  public componentClasses: (keyof CommonRegistry)[] = [
    'Velocity',
    'MovementSpeeds',
    'MovementMode',
    'Input',
    'Transform',
  ];
  protected simulationBound = true;
  protected localPlayerOnly = false;

  updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredComponentSystemUpdateEntityArgs<CommonRegistry>): void {
    if (world.hasComponent(entityId, 'InBed')) {
      const vel = world.getComponent(entityId, 'Velocity');
      if (vel && (vel.dx !== 0 || vel.dy !== 0)) {
        this.updateComponent(entityId, vel, { dx: 0, dy: 0 });
      }
      return;
    }

    const vel = world.getComponent(entityId, 'Velocity')!;
    const inputState = world.getComponent(entityId, 'Input')!;

    /**
     * CRITICAL: Skip server-side movement intent for player-controlled entities.
     * With client-authoritative movement, players manage their own velocity.
     * Simulated intent on the server creates discrepancies during rapid input
     * changes (e.g. switching left to right), leading to false speed rejections.
     */
    if (!this.services.isClient && world.hasComponent(entityId, 'PlayerControlled')) {
      return;
    }

    const speed = this.services.speed.getEffectiveSpeed(world, entityId);

    let newDy = 0;
    let newDx = 0;

    // If dead, we don't process input - entity should stop (friction/drag simulation)
    // KnockbackSystem will add its own velocity on top of this 0 base.
    const dead = world.getComponent(entityId, 'Dead');
    if (dead) {
      if (vel.dx !== 0 || vel.dy !== 0) {
        this.updateComponent(entityId, vel, { dx: 0, dy: 0 });
      }
      return;
    }

    if (inputState.up) {
      newDy = -speed;
    } else if (inputState.down) {
      newDy = speed;
    }

    if (inputState.left) {
      newDx = -speed;
    } else if (inputState.right) {
      newDx = speed;
    }

    // Determine target speed (normalized for diagonal movement)
    const isDiagonal = newDx !== 0 && newDy !== 0;
    const targetSpeed = isDiagonal ? speed / Math.sqrt(2) : speed;

    // Apply normalization to base intent
    if (isDiagonal) {
      const scale = 1 / Math.sqrt(2);
      newDx *= scale;
      newDy *= scale;
    }

    // Apply time-based corrections
    if (inputState.corrections) {
      const corrections = inputState.corrections;
      // Use targetSpeed (normalized if diagonal) for corrections to avoid diagonal speed bypass
      if (corrections.up) {
        newDy = applyCorrection(newDy, -targetSpeed, corrections.up, deltaTime);
      }
      if (corrections.down) {
        newDy = applyCorrection(newDy, targetSpeed, corrections.down, deltaTime);
      }
      if (corrections.left) {
        newDx = applyCorrection(newDx, -targetSpeed, corrections.left, deltaTime);
      }
      if (corrections.right) {
        newDx = applyCorrection(newDx, targetSpeed, corrections.right, deltaTime);
      }
    }

    if (vel.dx !== newDx || vel.dy !== newDy) {
      this.updateComponent(entityId, vel, { dx: newDx, dy: newDy });
    }
  }
}

function applyCorrection(
  currentVelocity: number,
  targetVelocity: number,
  correctionMs: number,
  deltaTime: number,
): number {
  if (deltaTime <= 0) return currentVelocity;
  // Calculate the extra movement needed to compensate for the duration difference.
  // correctionMs is the time (in ms) that the client input lasted longer than the server simulated.
  // We apply this as an instantaneous velocity boost for this frame to 'catch up' the position.

  const extraVelocity = (targetVelocity * (correctionMs / 1000)) / deltaTime;
  return currentVelocity + extraVelocity;
}
